/**
 * Playing a recording the API pointed at.
 *
 * Two kinds of object come back as an `audioUrl`, and they cannot be fetched the same
 * way. Course audio — a synthesised coach reading, a listening drop — is content-hash
 * addressed, shared by every learner and public, so the cheapest possible thing works:
 * hand the URL to an `<audio>` element and let it make range requests against an
 * immutable, cacheable response. A learner's own recording is none of those things; it
 * stays behind a bearer token and an ownership check, and an HTML media element has no
 * way to send a header, which is why it has to be fetched and played as bytes.
 *
 * The "▶ You" button was wired to `new Audio(url)` for a private key, so it answered 401
 * and played nothing — on the one screen whose whole purpose is hearing yourself against
 * a model reading.
 */

import { mediaUrl, tokenStore } from './client.js';

/**
 * Prefixes the audio route serves without a token, mirroring `PUBLIC_AUDIO_PREFIXES` in
 * the Worker. Anything else is somebody's recording and is fetched with credentials.
 */
const PUBLIC_PREFIXES = ['/v1/audio/content/', '/v1/audio/voice/'];

/** Whether a media element may fetch this itself. */
export function isPublicMedia(path: string): boolean {
  return PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * One player, so starting a second clip stops the first.
 *
 * Without this, pressing "You" and then "Native" plays both at once, which is the
 * opposite of a comparison.
 */
let current: { audio: HTMLAudioElement; objectUrl: string | null } | null = null;

export function stopMedia(): void {
  if (current === null) return;
  current.audio.pause();
  if (current.objectUrl !== null) URL.revokeObjectURL(current.objectUrl);
  current = null;
}

/**
 * Plays `path`, fetching it with credentials when it is not public.
 *
 * Resolves once playback has started, or returns the reason it did not, so a caller can
 * say something rather than leaving a button that looks broken.
 */
export async function playMedia(
  path: string | null | undefined,
  deps: { fetchImpl?: typeof fetch; token?: () => Promise<string | null> } = {},
): Promise<'playing' | 'unavailable' | 'blocked'> {
  const resolved = mediaUrl(path);
  if (resolved === null) return 'unavailable';

  stopMedia();

  let src = resolved;
  let objectUrl: string | null = null;

  if (!isPublicMedia(path ?? '')) {
    const token =
      deps.token === undefined
        ? ((await tokenStore.get())?.accessToken ?? null)
        : await deps.token();
    if (token === null) return 'unavailable';
    try {
      const fetchImpl = deps.fetchImpl ?? fetch;
      const response = await fetchImpl(resolved, { headers: { authorization: `Bearer ${token}` } });
      if (!response.ok) return 'unavailable';
      objectUrl = URL.createObjectURL(await response.blob());
      src = objectUrl;
    } catch {
      return 'unavailable';
    }
  }

  const audio = new Audio(src);
  current = { audio, objectUrl };
  // Released on the way out rather than left to the page lifetime: these are whole
  // recordings held in memory, and a learner may compare a dozen in one session.
  audio.addEventListener('ended', () => {
    if (current?.audio === audio) stopMedia();
  });

  try {
    await audio.play();
    return 'playing';
  } catch {
    // Autoplay policy can refuse a clip the learner did not initiate; theirs is a tap,
    // so this is rare, and pressing play again works.
    return 'blocked';
  }
}
